import React, { useState, useCallback } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronRight, RotateCcw, Clock, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const statusConfig = {
  pending_approval: { color: 'bg-warning-bg text-warning', label: 'Pending Approval' },
  approved: { color: 'bg-success-bg text-success', label: 'Approved' },
  rejected: { color: 'bg-danger-bg text-danger', label: 'Rejected' },
  committed: { color: 'bg-success-bg text-success', label: 'Committed' },
  rolled_back: { color: 'bg-muted text-muted-foreground', label: 'Rolled Back' },
};

/**
 * Staging review dashboard (Screen 18).
 * Shows batches pending approval with approve/reject/rollback actions.
 * @param {object} props
 * @param {Array} props.batches - staging batches
 * @param {Function} [props.onApprove]
 * @param {Function} [props.onReject]
 * @param {Function} [props.onRollback]
 */
const StagingReviewDashboard = ({ batches = [], onApprove, onReject, onRollback }) => {
  const [expandedBatch, setExpandedBatch] = useState(null);

  const stats = {
    total: batches.length,
    pending: batches.filter((b) => b.status === 'pending_approval').length,
    approved: batches.filter((b) => b.status === 'approved' || b.status === 'committed').length,
    rejected: batches.filter((b) => b.status === 'rejected').length,
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold flex items-center gap-2'>
          <Clock size={20} strokeWidth={1.5} />
          Staging Review
        </h2>
      </div>

      <div className='grid grid-cols-4 gap-3'>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold'>{stats.total}</p>
            <p className='text-xs text-muted-foreground'>Total Batches</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-warning'>{stats.pending}</p>
            <p className='text-xs text-warning'>Pending</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-success'>{stats.approved}</p>
            <p className='text-xs text-success'>Approved</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4 text-center'>
            <p className='text-2xl font-bold text-danger'>{stats.rejected}</p>
            <p className='text-xs text-danger'>Rejected</p>
          </CardContent>
        </Card>
      </div>

      <Card className='bg-card'>
        <CardContent className='p-0'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='w-8' />
                <TableHead>Import File</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead className='text-right'>Rows</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((batch) => {
                const cfg = statusConfig[batch.status] || statusConfig.pending_approval;
                const isExpanded = expandedBatch === batch.id;
                return (
                  <React.Fragment key={batch.id}>
                    <TableRow>
                      <TableCell>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='p-0 h-6 w-6'
                          onClick={() => setExpandedBatch(isExpanded ? null : batch.id)}
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </Button>
                      </TableCell>
                      <TableCell className='font-medium text-sm'>{batch.filename || batch.importId}</TableCell>
                      <TableCell className='text-sm'>{batch.submittedBy || '—'}</TableCell>
                      <TableCell className='text-right text-sm'>{batch.rowCount || 0}</TableCell>
                      <TableCell>
                        <Badge className={cfg.color}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground'>
                        {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <div className='flex gap-1'>
                          {batch.status === 'pending_approval' && (
                            <>
                              <Button variant='ghost' size='sm' className='text-success' onClick={() => onApprove?.(batch.id)}>
                                <CheckCircle size={14} strokeWidth={1.5} />
                              </Button>
                              <Button variant='ghost' size='sm' className='text-danger' onClick={() => onReject?.(batch.id)}>
                                <XCircle size={14} strokeWidth={1.5} />
                              </Button>
                            </>
                          )}
                          {(batch.status === 'approved' || batch.status === 'committed') && (
                            <Button variant='ghost' size='sm' className='text-warning' onClick={() => onRollback?.(batch.id)}>
                              <RotateCcw size={14} strokeWidth={1.5} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={7} className='bg-muted/20 p-4'>
                          <div className='text-sm'>
                            <p className='font-medium mb-2'>Batch Details</p>
                            <div className='grid grid-cols-3 gap-3'>
                              <div>
                                <span className='text-xs text-muted-foreground'>Import ID</span>
                                <p className='font-mono text-xs'>{batch.importId}</p>
                              </div>
                              <div>
                                <span className='text-xs text-muted-foreground'>Quality Score</span>
                                <p>{batch.qualityScore?.toFixed(1) || '—'}</p>
                              </div>
                              <div>
                                <span className='text-xs text-muted-foreground'>Emissions Impact</span>
                                <p>{batch.emissions?.toFixed(1) || '—'} tCO₂e</p>
                              </div>
                            </div>
                            {batch.notes && (
                              <div className='mt-2'>
                                <span className='text-xs text-muted-foreground'>Notes</span>
                                <p className='text-xs'>{batch.notes}</p>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              {batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className='text-center py-8 text-muted-foreground'>
                    No staging batches found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default StagingReviewDashboard;
